import React from 'react';
import { motion } from 'framer-motion';
import { Check, Circle, Crown } from 'lucide-react';

import { Avatar } from './Avatar';
import { SPRING_SORT } from './motion';
import { useProfilePreview } from './ProfilePreviewProvider';

/**
 * THE TABLE.
 *
 * The right rail of every game phase — lobby, race, tally. Same component,
 * same players, same order, so the room feels *continuous* as the phases
 * swap around it. On a phone the same information lives in the `.sub-strip`
 * ("2 / 4 done" + a row of avatars); the laptop just has room to show it
 * without stealing space from the stage.
 *
 * ── THE FAIRNESS RULE ────────────────────────────────────────────────────
 * No surface may reveal game information another surface doesn't.
 *
 * This rail shows: who's here, who has locked in, and the PUBLIC running
 * totals. It shows NOTHING else. Specifically it does not and must never
 * show an opponent's fill count, their draft answers, or a "typing…"
 * presence indicator — all of which we technically have in state and any
 * of which would hand the laptop player an edge the phone players can't
 * see. The moment that happens, family trust in the game is gone and no
 * amount of layout polish buys it back.
 *
 * Your OWN fill-count is fine (`youFilled` / `youTotal`) — it's your paper.
 * ─────────────────────────────────────────────────────────────────────────
 */
export interface RosterPlayer {
  id: string;
  name: string;
  avatar: string;
  isHost?: boolean;
  isBot?: boolean;
  isReady?: boolean;
  isConnected?: boolean;
  /** Public running total. Undefined in the lobby, where there isn't one. */
  score?: number;
}

export interface RosterRailProps {
  players: RosterPlayer[];
  meId?: string | null;
  /** Race phase: who has locked in. Leave undefined in the lobby. */
  submitted?: Set<string>;
  /** Show public running totals (race + tally). */
  showTotals?: boolean;
  /** Lobby: show READY / waiting instead of locked-in. */
  showReady?: boolean;
  /** Re-sort live by total as chips land (the tally's "who's winning" glance). */
  sortByTotal?: boolean;
  title?: string;
  /** Your own fill count — your paper, so it's yours to see. */
  youFilled?: number;
  youTotal?: number;
  footer?: React.ReactNode;
  /**
   * The room this rail is showing, when it is showing a real game.
   *
   * Passing it makes every row TAPPABLE — it opens the profile sheet, which is
   * where Block and Report live. Public games put strangers next to children,
   * and until now the roster was the one place you could see a stranger all
   * game long and not be able to do a single thing about them: the rail was
   * inert markup. If you can read someone's name while they are misbehaving,
   * you must be able to act on it from the same place.
   *
   * Omit it in contexts with no game (and no strangers) and the rows stay inert.
   */
  roomCode?: string | null;
}

export function RosterRail({
  players,
  meId,
  submitted,
  showTotals,
  showReady,
  sortByTotal,
  title = 'The table',
  youFilled,
  youTotal,
  footer,
  roomCode,
}: RosterRailProps) {
  const { open: openPreview } = useProfilePreview();

  const rows = sortByTotal
    ? [...players].sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
    : players;

  const doneCount = submitted ? players.filter(p => submitted.has(p.id)).length : 0;

  return (
    <div className="wr-roster">
      <div className="wr-between" style={{ marginBottom: 2 }}>
        <span className="eyebrow">{title}</span>
        {/* The done-count is the phone's `.sub-strip` label, verbatim. Nothing
            else goes here — a second number the phone doesn't have would be a
            fairness-rule violation dressed up as a stat. */}
        {submitted && (
          <span className="eyebrow" style={{ color: 'var(--ink-3)' }}>
            {doneCount}/{players.length} done
          </span>
        )}
      </div>

      {rows.map(row => {
        const isMe = !!meId && row.id === meId;
        const locked = submitted?.has(row.id) ?? false;
        const online = row.isBot || row.isConnected !== false;
        // A bot cannot be blocked or reported, and you cannot report yourself.
        const tappable = !!roomCode && !row.isBot && !isMe;
        return (
          <motion.div
            key={row.id}
            layout={!!sortByTotal}
            transition={SPRING_SORT as any}
            className={`roster-row ${isMe ? 'is-me' : ''}`.trim()}
            style={{ opacity: online ? 1 : 0.55, cursor: tappable ? 'pointer' : undefined }}
            onClick={tappable
              ? () => openPreview(row.id, { contextType: 'game', contextId: roomCode })
              : undefined}
            role={tappable ? 'button' : undefined}
            tabIndex={tappable ? 0 : undefined}
            onKeyDown={tappable
              ? (e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openPreview(row.id, { contextType: 'game', contextId: roomCode });
                }
              })
              : undefined}
            aria-label={tappable ? `${row.name} — open profile` : undefined}
          >
            <Avatar id={row.avatar} size={34} host={row.isHost} submitted={locked} />
            <span className="rname">
              {row.name}
              {isMe && <span style={{ color: 'var(--ink-3)', fontWeight: 500 }}> (you)</span>}
            </span>

            {showReady ? (
              row.isReady ? (
                <span className="ready" aria-label="ready">
                  <Check size={12} strokeWidth={3} />
                </span>
              ) : (
                <span className="waiting">{online ? 'Waiting…' : 'Away'}</span>
              )
            ) : null}

            {submitted && !showReady && (
              locked ? (
                <span className="ready" aria-label="locked in">
                  <Check size={12} strokeWidth={3} />
                </span>
              ) : (
                <Circle size={11} color="var(--ink-4)" aria-label="still writing" />
              )
            )}

            {showTotals && <span className="rtotal">{row.score ?? 0}</span>}
            {row.isHost && !showReady && !showTotals && (
              <Crown size={13} color="var(--accent)" aria-label="host" />
            )}
          </motion.div>
        );
      })}

      {/* YOUR paper, not theirs. Four dots, one per category. */}
      {typeof youFilled === 'number' && typeof youTotal === 'number' && (
        <div
          className="wr-row gap-2"
          style={{ alignItems: 'center', paddingTop: 6, borderTop: '1px solid var(--line)' }}
        >
          <span className="eyebrow" style={{ flex: 1 }}>Your paper</span>
          <span className="wr-row gap-1" aria-label={`${youFilled} of ${youTotal} filled`}>
            {Array.from({ length: youTotal }).map((_, i) => (
              <span
                key={i}
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 999,
                  background: i < youFilled ? 'var(--accent)' : 'var(--line-2)',
                }}
              />
            ))}
          </span>
        </div>
      )}

      {footer}
    </div>
  );
}
